import { navigation } from '../app/navigation.js';
import { error, log } from '../app/logger.js';
import { pipeUI } from '../ui/pipe_ui.js';
import { listenToEvent } from '../util/listen_to_event.js';
import { LocBase } from './loc_base.js';
import { LocMarkupView } from './loc_markup_view.js';
import { ReviewDialog } from './review_dialog.js';
import { ShareLinkDialog } from './share_link_dialog.js';

import '../controls/calendar_control.js';
import '../controls/collapsible_text.js';
import '../controls/image_list.js';
import '../controls/image_mosaic.js';
import '../controls/rating_control.js';
import './review_view.js';

class LocViewBase extends LocBase {
  static get selectors() {
    return this.MarkupClass.selectors;
  }

  static get elements() {
    const s = this.selectors;
    return {
      availabilityElement: s.Availability,
      availabilityCalendar: s.AvailabilityCalendar,
      backButton: s.BackButton,
      coverContainer: s.CoverContainer,
      coverImage: s.CoverImage,
      closeButton: s.CloseButton,
      descriptionElement: s.Description,
      goingThereButton: s.GoingThereButton,
      googleMapsLink: s.GoogleMapsLink,
      historyElement: s.History,
      historyContentElement: s.HistoryContent,
      fieldsContainer: s.FieldsContainer,
      fieldItems: s.FieldItems,
      mosaicCoverElement: s.MosaicCover,
      overallRatingElement: s.OverallRating,
      overallRatingLabel: s.OverallRatingLabel,
      picListElement: s.PicList,
      relsContainer: s.RelsContainer,
      relLinks: s.RelLinks,
      reviewsContainer: s.ReviewsContainer,
      reviewItemsContainer: s.ReviewItemsContainer,
      shareLocButton: s.ShareLocButton,
      summaryElement: s.Summary,
      suggestEditButton: s.SuggestEditButton,
      titleElement: s.Title,
      typeElement: s.Type,
      writeReviewButton: s.WriteReviewButton,
    };
  }

  static get MarkupClass() {
    return LocMarkupView;
  }

  initialize() {
    super.initialize();
    this.bindUI(this.constructor.snippets);
  }

  openGallery(picIdx) {
    return navigation.openGallery({
      pics: this.loc.pics,
      picidx: picIdx,
      title: this.loc.fullName,
    });
  }

  openRelatedLoc(loc) {
    log(`[locview] open related '${loc.name}':${loc.id} location`);
    navigation.map.showUpLocation({
      id: loc.id,
    });
  }

  pipeUI() {
    this.#unpipeUI();

    if (!this.loc) {
      return;
    }

    for (let name of this.constructor.snippets) {
      switch (name) {
        case 'availability':
          this.#pipeAvailability();
          break;
        case 'cover':
          this.#pipeCover();
          break;
        case 'description':
          this.#pipeDescription();
          break;
        case 'fields':
          this.#pipeFields();
          break;
        case 'fullDescription':
          this.#pipeFullDescription();
          break;
        case 'googleMapsLink':
          this.#pipeGoogleMapsLink();
          break;
        case 'history':
          this.#pipeHistory();
          break;
        case 'mosaicCover':
          this.#pipeMosaicCover();
          break;
        case 'picList':
          this.#pipePicList();
          break;
        case 'reviews':
          this.#pipeReviews();
          break;
        case 'summary':
          this.#pipeSummary();
          break;
        case 'title':
          this.#pipeTitle();
          break;
      }
    }

    // Update dynamic snippets which markup is completely regenerated on a new data pipe.
    this.bindUI(this.constructor.dynamicSnippets);
  }

  bindUI(snippets) {
    for (let name of snippets) {
      switch (name) {
        case 'cover':
          this.#bindCover();
          break;
        case 'editButtons':
          this.#bindEditButtons();
          break;
        case 'history':
          this.#bindHistory();
          break;
        case 'mainButtons':
          this.#bindMainButtons();
          break;
        case 'mosaicCover':
          this.#bindMosaicCover();
          break;
        case 'picList':
          this.#bindPicList();
          break;
        case 'title':
          this.#bindTitle();
          break;
      }
    }
  }

  #unpipeUI() {
    for (let name of Object.keys(this.#pipes)) {
      this.#pipes[name].unpipe();
      delete this.#pipes[name];
    }
  }

  #bindCover() {
    listenToEvent(this.coverImage, 'click', () => {
      let picIdx = this.loc.pics.indexOf(this.loc.cover);
      if (picIdx != -1) {
        this.openGallery(picIdx);
      }
    });

    listenToEvent(this.closeButton, 'click', () => this.fireClose());
    listenToEvent(this.backButton, 'click', () => this.fireCollapse());
  }

  #bindEditButtons() {
    listenToEvent(this.writeReviewButton, 'click', () => this.#writeReview(), {
      authenticate: true,
    });
    listenToEvent(this.suggestEditButton, 'click', () => this.fireEdit(), {
      authenticate: true,
    });
  }

  #bindHistory() {
    listenToEvent(
      this.historyElement,
      'toggle',
      () => this.historyElement.open && this.loc.reloadHistory()
    );
  }

  #bindMainButtons() {
    listenToEvent(this.goingThereButton, 'click', () => this.fireLocPick());
    listenToEvent(this.shareLocButton, 'click', () =>
      ShareLinkDialog.show(this.loc)
    );
  }

  #bindMosaicCover() {
    listenToEvent(this.mosaicCoverElement, 'picpick', ev => {
      if (!this.loc) {
        error(`[locpage:picpick] pic is picked before locpage is initialized`);
        return;
      }
      log(`[locpage:picpick] ${ev.detail} pic is picked`);
      this.openGallery(ev.detail);
    });
  }

  #bindPicList() {
    listenToEvent(this.picListElement, 'itemselect', ev =>
      this.openGallery(ev.detail.index)
    );
  }

  #bindTitle() {
    listenToEvent(
      this.relLinks,
      'click',
      ev =>
        this.openRelatedLoc({
          id: parseInt(ev.target.getAttribute('locid')),
          name: ev.target.textContent,
        }),
      {
        stopPropagation: true,
      }
    );
  }

  #pipeAvailability() {
    this.#pipes.availability?.unpipe();

    this.#pipes.availability = this.loc.pipe({
      availability: () => {
        let calendar = this.loc.calendar;
        log(
          `[locview] refresh availability for ${this.loc.id}:'${
            this.loc.name
          }, ${calendar ? 'has' : 'no'} calendar'`
        );
        this.availabilityElement.hidden = !calendar;
        if (calendar) {
          this.availabilityCalendar.set(calendar);
        }
      },
    });
  }

  #pipeCover() {
    this.#pipes.cover?.unpipe();
    this.#pipes.cover = this.loc.pipe({
      cover: () => this.setCover(this.loc.coverUrl),
    });
  }

  #pipeDescription() {
    this.#pipes.description?.unpipe();
    this.#pipes.description = this.loc.pipe({
      description: v => (this.descriptionElement.value = v),
    });
  }

  #pipeFields() {
    this.#pipes.fields?.unpipe();
    this.#pipes.fields = this.loc.pipe({
      fields: () => {
        for (let el of this.fieldItems) {
          if (el != this.googleMapsLink) el.remove();
        }
        this.fieldsContainer.insertAdjacentHTML(
          'afterbegin',
          this.markupGenerator.fieldsMarkup
        );
      },
    });
  }

  #pipeFullDescription() {
    this.#pipes.fullDescription?.unpipe();
    this.#pipes.fullDescription = this.loc.pipe({
      fullDescription: v => (this.descriptionElement.textContent = v),
    });
  }

  #pipeGoogleMapsLink() {
    this.#pipes.googleMapsLink?.unpipe();
    this.#pipes.googleMapsLink = this.loc.pipe({
      googleMapsUrl: v => this.googleMapsLink.setAttribute('href', v),
    });
  }

  #pipeHistory() {
    this.#pipes.history?.unpipe();
    this.#pipes.history = this.loc.pipe({
      history: () =>
        (this.historyContentElement.innerHTML =
          this.markupGenerator.historyMarkup),
    });
  }

  #pipeMosaicCover() {
    this.#pipes.mosaicCover?.unpipe();
    this.#pipes.mosaicCover = this.loc.pipe({
      pics: () => {
        this.mosaicCoverElement.setPics(this.loc.mosaicPics);
        this.mosaicCoverElement.inactive = !this.loc.hasPics();
      },
    });
  }

  #pipePicList() {
    this.#pipes.picList?.unpipe();
    this.#pipes.picList = this.loc.pipe({
      pics: () =>
        this.setCoverPromise.then(() =>
          this.picListElement.set(this.loc.listPics)
        ),
    });
  }

  #pipeReviews() {
    this.#pipes.reviews?.unpipe();
    this.#pipes.reviews = pipeUI({
      pipeable: this.loc.reviews,
      parentNode: this.reviewItemsContainer,
      uiFunc: () => `<review-view></review-view>`,
      elFunc: (reviewView, review) => reviewView.set(review),
      onCleared: () => (this.reviewsContainer.hidden = true),
      onStocked: () => (this.reviewsContainer.hidden = false),
    });
  }

  #pipeSummary() {
    this.#pipes.summaryReview?.unpipe();
    this.#pipes.summaryAttributes?.unpipe();

    // TODO: it will perform summary update twice. Either combine those into
    // a single pipe or separate UI setting for review summary and attributes.
    this.#pipes.summaryReview = this.loc.reviews.pipeables.summary.pipe(value =>
      this.summaryElement.set(value, this.loc.attributes)
    );
    this.#pipes.summaryAttributes = this.loc.pipe({
      attributes: value => this.summaryElement.set(this.loc.summary, value),
    });
  }

  #pipeTitle() {
    this.#pipes.overallRating?.unpipe();
    this.#pipes.title?.unpipe();

    this.#pipes.overallRating = this.loc.reviews.pipeables.overallRating.pipe(
      value => {
        this.overallRatingElement.value = value;
        this.overallRatingLabel.textContent = value;
      }
    );

    this.#pipes.title = this.loc.pipe({
      name: v => (this.titleElement.textContent = v),
      typeName: v => (this.typeElement.textContent = v),
      rels: () =>
        (this.relsContainer.outerHTML = this.markupGenerator.titleRelsMarkup),
    });
  }

  #writeReview() {
    return ReviewDialog.show({
      data: {
        loc: this.loc,
      },
      onaccept: reviewData => this.loc.addReview(reviewData),
    });
  }

  fireClose() {
    this.dispatchEvent(
      new Event('closerequest', {
        bubbles: true,
      })
    );
  }

  fireCollapse() {
    this.dispatchEvent(
      new Event('collapse', {
        bubbles: true,
      })
    );
  }

  fireEdit() {
    this.dispatchEvent(
      new Event('edit', {
        bubbles: true,
      })
    );
  }

  fireExpand() {
    this.dispatchEvent(
      new Event('expand', {
        bubbles: true,
      })
    );
  }

  fireLocPick() {
    this.dispatchEvent(
      new CustomEvent('locpick', {
        bubbles: true,
        detail: this.loc,
      })
    );
  }

  #pipes = {};
}

export { LocViewBase };
